import { Edge, Node } from '@xyflow/react';

import {
  HORIZONTAL_SPACING,
  NODE_HEIGHT,
  NODE_WIDTHS,
  VERTICAL_SPACING,
} from '@/src/modules/AutomationModuleNew/constants/flows';
import {
  BinaryConditionResultConnectionConditionType,
  JourneyNodeTypes,
  JourneySchema,
} from '@/src/modules/AutomationModuleNew/types/flowsTypes';
import {
  findRootNodeId,
  getMainNodeType,
} from '@/src/modules/AutomationModuleNew/utils/journeysHelper';

export const useTransformDataToFlow = (data?: JourneySchema) => {
  if (!data) return { nodes: [], edges: [] };
  const transformSchemaToFlow = (
    schema: JourneySchema
  ): {
    nodes: Node[];
    edges: Edge[];
  } => {
    const positionMap: Record<string, { x: number; y: number }> = {};
    const childMap: Record<string, string[]> = {};
    const visited = new Set<string>();

    for (const conn of schema.connections) {
      if (!childMap[conn.from]) childMap[conn.from] = [];
      childMap[conn.from].push(conn.to);
    }

    const rootId =
      schema.root === '' || !schema.root
        ? findRootNodeId(schema.nodes as unknown as Node[], schema.connections) ?? 'node0'
        : schema.root;

    positionMap[rootId] = { x: 0, y: 0 };
    visited.add(rootId);

    const getNodeWidth = (type: JourneyNodeTypes) => NODE_WIDTHS[type] ?? 280;

    function positionChildren(parentId: string) {
      const children = childMap[parentId] ?? [];
      const parentNode = schema.nodes.find((n) => n.id === parentId);
      if (!parentNode) return;
      const parentPos = positionMap[parentId];
      const parentWidth = getNodeWidth(parentNode.type);

      children.forEach((childId) => {
        if (visited.has(childId)) return;
        const childNode = schema.nodes.find((n) => n.id === childId);
        if (!childNode) return;

        const childWidth = getNodeWidth(childNode.type);
        let x = 0;
        let y = 0;

        if (childNode.position) {
          x = childNode.position.x;
          y = childNode.position.y;
          positionMap[childId] = { x, y };
          visited.add(childId);
          positionChildren(childId);
          return;
        }

        // if the child is a conditional node, center it below the parent.
        if (childNode.type === 'binaryConditional') {
          x = parentPos.x + parentWidth / 2 - childWidth / 2;
          y = parentPos.y + NODE_HEIGHT.normalNode + VERTICAL_SPACING.binaryConditional;
        }

        // if the parent is conditional node, distribute children to the left (No) or right (Yes).
        else if (parentNode.type === 'binaryConditional') {
          const conn = schema.connections.find((c) => c.to === childId && c.from === parentId);
          const condition = conn?.condition as BinaryConditionResultConnectionConditionType;

          const isYes = condition?.value === true;
          const offsetX = parentWidth + HORIZONTAL_SPACING.normalNode;
          const dir = isYes ? 1 : -1;
          x = dir * (parentPos.x + offsetX);
          y = parentPos.y + NODE_HEIGHT.binaryConditional + VERTICAL_SPACING.normalNode;
        }
        // common node (no conditional), center it bellow parent.
        else {
          x = parentPos.x + parentWidth / 2 - childWidth / 2;
          y = parentPos.y + NODE_HEIGHT.normalNode + VERTICAL_SPACING.normalNode;
        }

        positionMap[childId] = { x, y };
        visited.add(childId);
        positionChildren(childId);
      });
    }

    positionChildren(rootId);

    const nodes: Node[] = schema.nodes.map((node) => ({
      id: node.id,
      type: getMainNodeType(node.type),
      position: positionMap[node.id] ?? node.position ?? { x: 0, y: 0 },
      data: node,
    }));

    const edges: Edge[] = schema.connections.map((conn) => {
      const getLabel = () => {
        const condition = conn.condition as BinaryConditionResultConnectionConditionType;
        if (condition?.type === 'binaryConditional') {
          return condition.value ? 'Yes' : 'No';
        }
        return '';
      };

      const label = getLabel();

      const isCondition =
        (conn.condition as BinaryConditionResultConnectionConditionType)?.type ===
        'binaryConditional';

      let handleId: string | undefined;

      if (isCondition) {
        handleId = (conn.condition as BinaryConditionResultConnectionConditionType).value
          ? 'yes'
          : 'no';
      } else {
        handleId = undefined;
      }

      return {
        id: conn.id,
        source: conn.from,
        target: conn.to,
        sourceHandle: handleId,
        type: 'customEdge',
        label,
        data: conn,
      };
    });

    return { nodes, edges };
  };

  const { nodes, edges } = transformSchemaToFlow(data);

  return { nodes, edges };
};
